import { Fragment } from "react";
import Button from "@components/ui/button";
import DialogBase from "@components/ui/dialog-base";
import { ScrollArea } from "@components/ui/scroll-area";
import { IconArrowRight, IconLoader2 } from "@tabler/icons-react";
import { useAcceptImport } from "../hooks/use-accept-import";
import { useImportDesignSystem } from "../hooks/use-import-design-system";
import { useVersionRowActions } from "../hooks/use-version-row-actions";
import {
  selectAllFinal,
  selectAllSettled,
  selectAttentionCount,
  selectFinalCount,
} from "../stores/file-to-storydoc-generation.store";
import {
  findImportByStore,
  useImportRegistry,
} from "../stores/import-registry.store";
import {
  useImportStore,
  useImportStoreApi,
} from "../stores/import-store-context";
import { closeImportDialog } from "../utils/import-dialog-id";
import { trackImport } from "../utils/track-import";
import { ImportDialogFooter } from "./import-dialog-footer";
import { VersionRow } from "./version-row";

export const VersionsStep = () => {
  const store = useImportStoreApi();
  const { rows, designColors, status, selectedDesignSystemId } = useImportStore(
    (s) => ({
      rows: s.rows,
      designColors: s.designColors,
      status: s.status,
      selectedDesignSystemId: s.selectedDesignSystemId,
    }),
  );
  const { designRef } = useImportDesignSystem();
  const { accept, isAccepting } = useAcceptImport();
  const {
    requesting,
    handleSelect,
    handleStatic,
    handleRegenerate,
    handleUndo,
    handleRate,
  } = useVersionRowActions();
  const allFinal = selectAllFinal(rows);
  const importing =
    !selectAllSettled(rows) ||
    requesting.size > 0 ||
    rows.some((r) => r.regenerating != null);
  const attention = selectAttentionCount(rows);
  const finalCount = selectFinalCount(rows);
  const imported = `${finalCount} ${finalCount === 1 ? "slide" : "slides"} imported`;
  const themeFailed =
    status === "done" && designColors == null && selectedDesignSystemId == null;

  const handleAccept = () => {
    trackImport("footer interaction", { control: "accept" }, store);
    void accept();
  };

  const handleDiscard = () => {
    trackImport("closed", { step: "discard" }, store);
    const entry = findImportByStore(store);
    if (entry) useImportRegistry.getState().removeImport(entry.id);
    closeImportDialog();
  };

  return (
    <DialogBase.Content className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-p-0">
      {themeFailed && (
        <p
          role="status"
          className="tw-mx-auto tw-mb-4 tw-rounded-lg tw-bg-blue-50 tw-px-3 tw-py-2 tw-text-xs tw-text-blue-800"
        >
          We couldn't read your file's theme, so your workspace default is
          applied.
        </p>
      )}
      <ScrollArea
        fill
        ref={designRef}
        className="story -tw-mb-6 tw-min-h-0 tw-flex-1"
      >
        <div className="tw-mx-auto tw-flex tw-w-fit tw-flex-col tw-gap-8 tw-px-2 tw-pb-28 tw-pt-2">
          {rows.map((row, i) => (
            <Fragment key={row.pageNumber}>
              <VersionRow
                row={row}
                requesting={requesting.has(row.pageNumber)}
                onSelect={(k) => handleSelect(i, k)}
                onStatic={(on) => handleStatic(i, on)}
                onRegenerate={() => void handleRegenerate(i)}
                onUndo={() => void handleUndo(i)}
                onRate={(rating) => handleRate(i, rating)}
              />
              <div
                aria-hidden="true"
                className="tw-h-px tw-w-full tw-bg-blue/5"
              />
            </Fragment>
          ))}
        </div>
      </ScrollArea>
      <div
        aria-hidden="true"
        className="tw-pointer-events-none tw-absolute tw-inset-x-0 tw-bottom-0 tw-h-[107px] tw-bg-gradient-to-b tw-from-white/0 tw-to-white"
      />
      <ImportDialogFooter>
        <span className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-medium tw-text-blue">
          {importing ? (
            <>
              <IconLoader2 className="tw-size-4 tw-shrink-0 tw-animate-spin tw-text-violet-700" />
              Importing slides
            </>
          ) : attention > 0 ? (
            `${imported} · ${attention} ${attention === 1 ? "needs" : "need"} your attention`
          ) : (
            imported
          )}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          <Button
            variant="secondary"
            onClick={handleDiscard}
            disabled={isAccepting}
          >
            Discard
          </Button>
          <Button
            onClick={handleAccept}
            disabled={!allFinal || isAccepting}
            className="tw-group"
          >
            {isAccepting ? "Creating…" : "Accept and edit"}
            <IconArrowRight className="tw-ml-1.5 tw-size-4 !tw-fill-none tw-text-white group-disabled:tw-text-blue-700" />
          </Button>
        </div>
      </ImportDialogFooter>
    </DialogBase.Content>
  );
};
